// This hook is created to migrate bookmarks from localStorage to the database, so it can be removed some time after deployment

import { useMutation, useReactiveVar } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Ad } from '@/api/types/common.types';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { sortByObjectProperty } from '@/utils/helpers';
import { CreateBookmarkResponse } from '@/api/types/ads.types';
import { CREATE_BOOKMARK } from '@/api/mutations/bookmarks.mutations';
import { csrfTokenVar } from '@/config/csrfTokenVar';

type AdWithOrder = Ad & { order: number };
type StoredBookmarks = Record<number, AdWithOrder>;

export function getStoredBookmarks(): AdWithOrder[] {
  try {
    if (
      typeof localStorage === 'undefined' ||
      !localStorage ||
      !localStorage.getItem(STORAGE_KEYS.BOOKMARKS)
    )
      return [];

    const storedObject = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.BOOKMARKS) || '{}'
    ) as StoredBookmarks;

    return sortByObjectProperty<AdWithOrder>(
      Object.values(storedObject).map(item => ({ ...item })),
      'order'
    );
  } catch {
    return [];
  }
}

export function useLSBookmarks() {
  const [isChecked, setIsChecked] = useState(false);
  const [createBookmark] = useMutation<CreateBookmarkResponse>(
    CREATE_BOOKMARK,
    {
      errorPolicy: 'ignore',
    }
  );
  const csrfToken = useReactiveVar(csrfTokenVar);

  useEffect(() => {
    if (csrfToken) {
      (async () => {
        const bookmarks = getStoredBookmarks();

        if (!isChecked && bookmarks.length > 0) {
          const adIds = bookmarks.map(({ id }) => id);
          await createBookmark({
            variables: { adIds },
          });
        }

        localStorage.removeItem(STORAGE_KEYS.BOOKMARKS);
        setIsChecked(true);
      })();
    }
  }, [csrfToken]);

  return isChecked;
}
